import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CacBasis, LLCurrency, LLRequest, LeadLifetimePreset } from '@/types/lead-lifetime';

const KEYS = {
    preset: 'lltv_preset',
    project: 'lltv_project',
    page: 'lltv_page',
    objectives: 'lltv_objectives',
    basis: 'lltv_basis',
    currency: 'lltv_currency',
};

/** Prefixos das queries da tela (usados pelo useIsFetching que trava os filtros). */
export const LL_QUERY_KEYS = ['lead_lifetime_summary', 'lead_lifetime_curve', 'lead_lifetime_daily', 'lead_lifetime_pages'];

const PRESETS: LeadLifetimePreset[] = ['today', 'yesterday', '7d', 'this_month', 'last_month', '60d', '90d'];

const read = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const readArray = (key: string): string[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
};

export interface LeadLifetimeFiltersState {
    preset: LeadLifetimePreset;
    setPreset: (p: LeadLifetimePreset) => void;
    projectId: number | undefined;
    setProjectId: (id: number | undefined) => void;
    pageId: string | undefined;
    setPageId: (id: string | undefined) => void;
    objectives: string[];
    setObjectives: (values: string[]) => void;
    basis: CacBasis;
    setBasis: (b: CacBasis) => void;
    currency: LLCurrency;
    setCurrency: (c: LLCurrency) => void;
    /** Recorte da análise (com a página selecionada). */
    request: LLRequest;
    /** Mesmo recorte sem página — base do comparativo no fim da tela. */
    comparisonRequest: LLRequest;
}

/**
 * Filtros da tela, persistidos em localStorage (padrão das telas de relatório): nada
 * passa pela URL. Padrão: mês atual, todas as páginas, todos os objetivos, CAC por lead.
 * Trocar de projeto limpa a página (ela pode não pertencer ao projeto novo).
 */
export function useLeadLifetimeFilters(): LeadLifetimeFiltersState {
    const [preset, setPreset] = useState<LeadLifetimePreset>(() => {
        const stored = read(KEYS.preset) as LeadLifetimePreset | null;
        return stored && PRESETS.includes(stored) ? stored : 'this_month';
    });
    const [projectId, setProjectIdState] = useState<number | undefined>(() => {
        const raw = Number(read(KEYS.project));
        return Number.isFinite(raw) && raw > 0 ? raw : undefined;
    });
    const [pageId, setPageId] = useState<string | undefined>(() => read(KEYS.page) || undefined);
    const [objectives, setObjectives] = useState<string[]>(() => readArray(KEYS.objectives));
    const [basis, setBasis] = useState<CacBasis>(() => (read(KEYS.basis) === 'conversation' ? 'conversation' : 'lead'));
    const [currency, setCurrency] = useState<LLCurrency>(() => (read(KEYS.currency) === 'USD' ? 'USD' : 'BRL'));

    useEffect(() => {
        try {
            localStorage.setItem(KEYS.preset, preset);
            localStorage.setItem(KEYS.project, projectId ? String(projectId) : '');
            localStorage.setItem(KEYS.page, pageId || '');
            localStorage.setItem(KEYS.objectives, JSON.stringify(objectives));
            localStorage.setItem(KEYS.basis, basis);
            localStorage.setItem(KEYS.currency, currency);
        } catch {
            /* sem storage */
        }
    }, [preset, projectId, pageId, objectives, basis, currency]);

    const projectRef = useRef(projectId);
    useEffect(() => {
        projectRef.current = projectId;
    }, [projectId]);

    const setProjectId = useCallback((id: number | undefined) => {
        if (projectRef.current !== id) setPageId(undefined);
        setProjectIdState(id);
    }, []);

    const comparisonRequest = useMemo<LLRequest>(
        () => ({ preset, project_id: projectId, objective: objectives }),
        [preset, projectId, objectives]
    );
    const request = useMemo<LLRequest>(() => ({ ...comparisonRequest, page_id: pageId }), [comparisonRequest, pageId]);

    return {
        preset, setPreset,
        projectId, setProjectId,
        pageId, setPageId,
        objectives, setObjectives,
        basis, setBasis,
        currency, setCurrency,
        request, comparisonRequest,
    };
}
